/**
 * Application Configuration
 *
 * This file contains all environment-specific configuration settings.
 * Use this file instead of hard-coding values in components.
 */

// Product display name - the single source of truth for branding strings
// (route titles, toolbar wordmark, document.title fallback).
export const TAGLINE = "Turn AI images into realistic photos.";
export const BRAND_ICON = "mdi-auto-fix";
export const PRODUCT_NAME = "PhotoRealify";
export const SUPPORT_EMAIL = "support@photorealify.com";

// Unit of work this app's credits pay for, used in plan/pricing copy
// (e.g. "concurrent upscales", "≈ N upscales / mo"). See root
// src/config.js for the default this overrides.
export const UNIT_NOUN = "upscale";
export const UNIT_NOUN_PLURAL = "upscales";

// This app's site slug (apps/upscale/app.json: site_slug "upscale"). Read by
// the shared api service (frontend-shared/src/services/api.js) to attach an
// X-Site-Slug header on every request in dev, so the gateway's
// SiteContextMiddleware resolves this site even though the dev server runs
// on localhost like every other app (Host-header resolution only works in
// production, where each app has its own domain).
export const SITE_SLUG = "upscale";

// Base URL of the CopilotClone agent platform. In development the Vue dev
// server proxies /agent to the local agent platform (see vue.config.js); in
// production nginx does the same on the API host. Used by the optional
// ChatWidget module, if/when this app mounts one.
export function agentApiBaseUrl() {
  if (process.env.NODE_ENV === "development") {
    return "/agent";
  }
  return `${config.api.baseURL}/agent`;
}

// Marketing approximations for a typical ~1.5MP image at each realism
// level's DEFAULT output size (Basic/High 2x, Extreme 4x). MUST track the
// backend's conversion_credits constants and UPSCALE_MARGIN
// (apps/upscale/backend/handler.py) - this is the single source for every
// "≈ N conversions" label in the SPA.
export const CONVERSION_CREDITS = { basic: 2, high: 2, extreme: 12 };

// Round DOWN to multiples of 5 so counts read as estimates and match the
// landing page's tiers (apps/upscale/landing/utils/pricingTiers.js:
// 62.5 -> 60) without ever over-promising.
export function approxConversions(credits, level) {
  const perUnit = CONVERSION_CREDITS[level || "high"] || CONVERSION_CREDITS.high;
  const n = Number(credits) / perUnit;
  if (!isFinite(n) || n <= 0) return 0;
  if (n >= 30) return Math.floor(n / 5) * 5;
  return Math.floor(n);
}

// One-time credit packs offered at the out-of-credits moment (UI presets
// over the shared /credits/purchase amount endpoint). 35cr = $5.00 and
// 70cr = $10.00 at the non-subscriber rate (7 credits per $1).
export const CREDIT_PACKS = [35, 70];

// One-time welcome pack (first purchase only; the billing service enforces
// it): 21 credits = $3.00 at the non-subscriber rate, ~10 conversions.
export const FIRST_PACK_CREDITS = 21;

// Per-plan marketing copy for the in-app plan cards (/subscribe,
// /subscription, /pricing and the subscribe dialog). When this map exists,
// the shared SubscriptionPlanCard renders EXACTLY these bullets and drops
// its generic rows (concurrency, API access, pack rates, reference-job
// estimates), which describe the reference app, not this one.
//
// KEEP IN SYNC with the landing page's tiers:
//   apps/upscale/landing/utils/pricingTiers.js
// (separate builds, so the copy is duplicated deliberately).
export const PLAN_MARKETING = {
  starter: {
    tagline: "The easy way in - a light monthly allowance",
    creditsLine: "≈ 30 High-realism conversions / mo",
    features: [
      "or ~5 Extreme (4x) conversions",
      "Unlocks Extreme realism and 4x output",
      "60 credits / month included",
      "Buy extra credits anytime",
      "Batch upload (up to 10 images at once)",
      "JPEG, PNG, and WEBP up to 25MB"
    ]
  },
  basic: {
    tagline: "For getting your feet wet - a steady monthly conversion allowance",
    creditsLine: "≈ 100 High-realism conversions / mo",
    features: [
      "or ~16 Extreme (4x) conversions",
      "Unlocks Extreme realism and 4x output",
      "200 credits / month included",
      "Buy extra credits anytime",
      "Batch upload - 2 convert in parallel",
      "Interactive before/after comparison",
      "JPEG, PNG, and WEBP up to 25MB"
    ]
  },
  pro: {
    tagline: "For creators and shops publishing realistic images every week",
    creditsLine: "≈ 250 High-realism conversions / mo",
    features: [
      "or ~40 Extreme (4x) conversions",
      "Unlocks Extreme realism and 4x output",
      "500 credits / month included",
      "Buy extra credits anytime",
      "Batch upload - 3 convert in parallel",
      "JPEG, PNG, and WEBP up to 25MB"
    ]
  },
  ultra: {
    tagline: "For high-volume pipelines - agencies, stores, and power users",
    creditsLine: "≈ 1000 High-realism conversions / mo",
    features: [
      "or ~165 Extreme (4x) conversions",
      "Unlocks Extreme realism and 4x output",
      "2000 credits / month included",
      "Buy extra credits anytime",
      "Batch upload - 5 convert in parallel",
      "Priority support",
      "JPEG, PNG, and WEBP up to 25MB"
    ]
  }
};

// Overrides for the shared /faq (FAQView.vue), which otherwise ships
// generic reference-app copy ("upload your files, jobs get processed") -
// wrong framing for a single-image converter, and its draft answers carry
// literal "[TODO: legal review]" text that must never reach a real user.
// {question, answer} shape matches FAQView's own array (answer is raw HTML).
export const FAQ_ITEMS = [
  {
    question: "Isn't this just an upscaler?",
    answer:
      "<p>No - and that distinction is the entire product. An upscaler enlarges the pixels you already have: it sharpens edges and smooths noise, but plastic skin stays plastic and painted-on hair stays painted on. You end up with a bigger version of the same AI-looking image.</p>" +
      "<p><strong>Generative Detail Reconstruction™</strong> works the other way around. It re-renders your image as a photograph first - rebuilding skin, light and materials from scratch - and only then reconstructs it at high resolution with generated micro-detail. Resolution is the side effect; realism is the product.</p>"
  },
  {
    question: "What is PhotoRealify?",
    answer:
      "<p>PhotoRealify converts AI-generated images into realistic, high-resolution photos using Generative Detail Reconstruction - a two-stage generative pipeline:</p>" +
      "<ul>" +
      "<li><strong>Stage 1 - Reality Pass:</strong> your image is re-rendered as a photograph; skin, light and materials are rebuilt from scratch</li>" +
      "<li><strong>Stage 2 - Detail Synthesis:</strong> resolution is reconstructed with generated micro-detail (pores, strands, fibers) at up to 4x</li>" +
      "<li>One upload, no prompts or editing skills; compare on a slider and download in about a minute</li>" +
      "</ul>"
  },
  {
    question: "Will it change my image?",
    answer:
      "<p>It rebuilds the surfaces, not the picture. Your composition, subject and likeness are preserved - what changes is the material the image is made of.</p>" +
      "<p>Higher realism levels rebuild more aggressively: Basic nudges the image toward photography, while Extreme replaces essentially every texture with photographic detail. If you want the pixels untouched and only the size increased, choose <em>Resolution only</em>.</p>"
  },
  {
    question: "What are the realism levels?",
    answer:
      "<p>You control how far the conversion goes; each level picks a sensible default output size (adjustable):</p>" +
      "<ul>" +
      "<li><strong>Resolution only</strong> - GDR off; your image keeps its exact look, just far more pixels</li>" +
      "<li><strong>Basic</strong> (2x default) - a subtle nudge toward photography</li>" +
      "<li><strong>High</strong> (4x default) - the full realistic re-render most people use day to day</li>" +
      "<li><strong>Extreme</strong> (4x default) - maximum photorealism with hyper-detailed textures</li>" +
      "</ul>"
  },
  {
    question: "Which images work best, and what can I upload?",
    answer:
      "<p>Any AI image works - Midjourney, DALL·E, Stable Diffusion, game renders and CGI. Faces and people show the most dramatic difference, because that's where the \"AI look\" (airbrushed skin, painted hair) is most visible. JPEG, PNG and WEBP up to 25MB.</p>"
  },
  {
    question: "What do the output sizes mean?",
    answer:
      "<p>2x doubles your image's width and height, and 4x, our maximum, quadruples them (16x the pixels) - large enough for prints and big displays. Bigger outputs cost more credits and take a little longer.</p>"
  },
  {
    question: "What does a conversion cost, and how do credits work?",
    answer:
      "<p>Credits are SaaS usage units - not money, stored value, or transferable value, and they can't be withdrawn or redeemed for cash. Your first conversion is <strong>free</strong> - no card required. Free conversions use Basic or High realism at 2x and include a small watermark; any pack or plan removes the watermark from all your images and unlocks Extreme realism and 4x output. Each paid conversion draws from your credits based on realism level and output size:</p>" +
      "<ul>" +
      "<li><strong>Basic</strong> (defaults to 2x): about 2 credits</li>" +
      "<li><strong>High</strong> (defaults to 2x): about 2 credits</li>" +
      "<li><strong>Extreme</strong> (defaults to 4x): about 12 credits</li>" +
      "<li>Larger images and larger output sizes cost proportionally more - the exact price is always shown before you run anything</li>" +
      "<li>Included monthly credits reset each billing cycle and do not roll over</li>" +
      "</ul>"
  },
  {
    question: "Can I buy more credits or change plans?",
    answer:
      "<p>Yes - on any paid plan. From the Billing page you can buy additional credits whenever you run out, or upgrade your plan for a larger monthly allowance. Payment is processed securely through Stripe and credits are available immediately.</p>"
  },
  {
    question: "What happens if I cancel my subscription?",
    answer:
      "<p>You keep full access until the end of your current billing period, and any remaining included credits can be used until then (they don't roll over). Your converted images stay in your gallery, and you can resubscribe at any time.</p>"
  },
  {
    question: "Is my image kept private?",
    answer:
      "<p>Yes. Your images are processed only to fulfil your request and stored privately in your gallery, where only you can see them and where you can delete them at any time. We never share your images or use them for anything else.</p>"
  },
  {
    question: "Can a conversion be declined?",
    answer:
      "<p>Rarely. Our safety system declines certain content (for example sexual imagery) - you'll see a clear message and those runs aren't charged. Everything else converts automatically, and transient hiccups are retried for you behind the scenes.</p>"
  },
  {
    question: "Do I own my converted images?",
    answer:
      "<p>Yes. You keep the rights to results generated from your own uploads, and you can use them for any lawful purpose including commercial work. In turn, you must own (or hold a licence for) every image you upload. The full licensing terms are in our Terms of Service.</p>"
  },
  {
    question: "Is my data secure?",
    answer:
      "<p>Your uploads and results travel over encrypted connections, are only accessible through your account, and are never sold or shared with other users. To perform the conversion, your image is processed by third-party AI infrastructure providers solely to produce the result you asked for. Your images stay in your gallery until you delete them. See our Privacy Policy for full details.</p>"
  }
];

const config = {
  productName: PRODUCT_NAME,
  // Free-tier hook shown on the auth screens (no starter credits here -
  // the free tier is one trial conversion).
  authTrustHighlight: "first conversion free",
  PRODUCT_NAME: PRODUCT_NAME,
  TAGLINE: TAGLINE,
  BRAND_ICON: BRAND_ICON,
  SUPPORT_EMAIL: SUPPORT_EMAIL,
  UNIT_NOUN: UNIT_NOUN,
  UNIT_NOUN_PLURAL: UNIT_NOUN_PLURAL,
  SITE_SLUG,

  // In-app plan-card copy (see PLAN_MARKETING above).
  PLAN_MARKETING: PLAN_MARKETING,
  // This app has no usable free PLAN - the free row stays in the DB as
  // platform plumbing, but every plan UI hides it. The trial ("first
  // conversion free") replaces it.
  HIDE_FREE_PLAN: true,
  // Anonymous trial: visitors get an auto-created guest session instead of
  // a login wall on routes flagged meta.allowAnonymous. Must match the
  // backend opt-in (SITE_UPSCALE_ANONYMOUS_TRIAL).
  ALLOW_ANONYMOUS: true,
  // One-time credit packs are sold to registered free accounts too (at the
  // non-subscriber rate) - the "second image" offer next to the
  // subscription. Must match the backend gate,
  // SITE_UPSCALE_TOPUPS_PAID_ONLY=False; anonymous guests can never buy.
  TOPUPS_PAID_ONLY: false,
  // Free conversions left (the trial plus the sign-up bonus), shown in the
  // header next to Credits. The endpoint reads the same allowance preview as
  // the price estimate.
  FREE_ALLOWANCE: {
    endpoint: "/apps/upscale/allowance",
    label: "Free conversions",
    unit: "free conversion"
  },
  // Shared-component hook: render a credit amount in the user's terms
  // ("≈ N conversions"). Shared views show it wherever credits appear;
  // apps without the hook keep plain credits. Returns null below one
  // conversion so zero balances don't advertise "≈ 0".
  CREDIT_EQUIVALENT: function (credits) {
    var n = approxConversions(credits, "high");
    if (n < 1) return null;
    return "≈ " + n + " High-realism conversion" + (n === 1 ? "" : "s");
  },
  // Referral programme: both sides earn a small credit bonus (the backend
  // pays SITE_UPSCALE_REFERRAL_CREDITS_PER_SIDE per side, capped at 5
  // successful referrals). Must match the backend switch
  // (SITE_UPSCALE_REFERRALS_DISABLED) - this only shows the UI surfaces.
  REFERRALS_ENABLED: true,
  // Images are re-rendered by AI models: shared screens keep their AI wording.
  AI_PROCESSING: true,
  // Mirrors SITE_UPSCALE_REFERRAL_CREDITS_PER_SIDE for static UI (the
  // toolbar chip); the invite page itself reads the live backend number.
  REFERRAL_CREDITS_PER_SIDE: 2,
  // No site-wide out-of-credits banner for free users: this app sells at
  // the moments that matter instead (the Enhance page's pre-upload notice,
  // the paywall dialog and the result-page offer), and a permanent bar
  // pushing subscriptions contradicted their pack-first offer.
  CREDIT_ALERT_FREE_ENABLED: false,

  // /faq override (see FAQ_ITEMS above) - the shared FAQView.vue default is
  // generic "upload files, jobs get processed" copy with literal
  // "[TODO: legal review]" placeholders, wrong for this app either way.
  FAQ_ITEMS: FAQ_ITEMS,

  // /subscribe hero override (SubscribeView.vue). The shared default
  // ("...runs the job, checks the output...") describes an automatic
  // QA/regeneration step this app does not have.
  SUBSCRIBE_HERO: {
    eyebrow: "You're in - pick your plan to get started",
    title: "Keep turning AI images into real photos",
    subtitle:
      "Upload an image and choose a realism level. PhotoRealify re-renders it as a photograph and reconstructs the detail at up to 4x - usually in about a minute."
  },
  SUBSCRIBE_FINAL_CTA_TITLE: "Ready to convert your next image?",
  // Overrides SubscribeView's default trust-signal chips, whose "Isolated,
  // reproducible runs" is false here - generative output varies between
  // runs by nature (also stated in our Terms).
  SUBSCRIBE_TRUST_SIGNALS: [
    { icon: "mdi-shield-check-outline", label: "Your images, your results" },
    { icon: "mdi-image-multiple-outline", label: "All realism levels, up to 4x" },
    { icon: "mdi-lock-outline", label: "Encrypted connections (HTTPS)" },
    { icon: "mdi-eye-outline", label: "Compare before/after before you download" },
    { icon: "mdi-swap-horizontal", label: "Cancel or change anytime" },
    { icon: "mdi-credit-card-lock-outline", label: "Secure checkout via Stripe" }
  ],

  // /invite-friends share-message tagline (InviteFriendsView.vue default
  // describes "AI processing jobs on your files" - not this product).
  INVITE_TAGLINE: "it turns AI-generated images into realistic photos",

  // /pricing FAQ overrides (PricingView.vue). The default text claims a
  // "regenerate output you're not happy with" action that doesn't exist
  // here (a failed run auto-retries; there's no user-facing re-roll).
  PRICING_CREDITS_EXPLAINER:
    "Credits are SaaS usage units for PhotoRealify conversions. They are not money, stored value, a wallet balance, or transferable value, and cannot be withdrawn, transferred, resold, or redeemed for cash. Each conversion consumes credits based on the realism level and output size you choose.",
  PRICING_CREDITS_CALC_EXPLAINER:
    "Credits are consumed based on realism level and output size: a higher realism level or a larger output size costs more credits. The exact price is always shown before you start a conversion, and credits are only deducted when a conversion completes successfully.",

  // API Configuration
  api: {
    // When VUE_APP_API_BASE_URL is unset, derive the API host from the page's
    // own hostname so the app works from both localhost (dev machine) and the
    // LAN IP (e.g. a phone hitting http://192.168.0.148:4512). The API listens
    // on 0.0.0.0:4500, so the same :4500 port is reachable on whatever host
    // served the frontend.
    baseURL:
      process.env.VUE_APP_API_BASE_URL ||
      `http://${window.location.hostname}:4500`,
    // No timeout for API requests - let the server handle timeouts
    timeout: 0
  },

  // Feature flags (product features land here as they ship)
  features: {}
};

export default config;
